import { useState } from 'react'
import { Button } from 'react-admin'
import {
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { Settings, DragIndicator, Refresh, Save, Close } from '@mui/icons-material'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Sortable column item component
function SortableColumnItem({ column, onToggleVisibility }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.field,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        py: 0.5,
        px: 1,
        borderRadius: 1,
        bgcolor: isDragging ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      secondaryAction={
        <Checkbox edge="end" checked={column.visible} onChange={() => onToggleVisibility(column.field)} size="small" />
      }
    >
      <ListItemIcon {...attributes} {...listeners} sx={{ minWidth: 32, cursor: 'grab' }}>
        <DragIndicator fontSize="small" color="action" />
      </ListItemIcon>
      <ListItemText
        primary={column.label}
        primaryTypographyProps={{
          variant: 'body2',
          sx: {
            textDecoration: column.visible ? 'none' : 'line-through',
            color: column.visible ? 'text.primary' : 'text.disabled',
          },
        }}
      />
    </ListItem>
  )
}

export function ColumnSettingsButton({
  allColumns,
  onToggleVisibility,
  onReorder,
  onSave,
  onReset,
  onDiscard,
  hasChanges,
  saving,
  loading,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSave = async () => {
    await onSave()
    handleClose()
  }

  const handleReset = async () => {
    await onReset()
    handleClose()
  }

  const handleDiscard = () => {
    onDiscard()
    handleClose()
  }

  // Handle drag end - convert from dnd-kit format to the format expected by useColumnPreferences
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = allColumns.findIndex((col) => col.field === active.id)
      const newIndex = allColumns.findIndex((col) => col.field === over.id)

      // Call onReorder with the format expected by the hook (simulating react-beautiful-dnd format)
      onReorder({
        source: { index: oldIndex },
        destination: { index: newIndex },
      })
    }
  }

  // Count visible columns for display
  const visibleCount = allColumns.filter((col) => col.visible).length

  return (
    <>
      <Button
        label={`Columns (${visibleCount}/${allColumns.length})`}
        onClick={handleOpen}
        size="small"
        sx={{
          minWidth: 'auto',
          '& .MuiButton-startIcon': { marginRight: 0.5 },
        }}
      >
        <Settings fontSize="small" />
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ width: 320, maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              Column Settings
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Reset to defaults">
                <IconButton size="small" onClick={handleReset} disabled={saving || loading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              {hasChanges && (
                <>
                  <Tooltip title="Discard changes">
                    <IconButton size="small" onClick={handleDiscard} disabled={saving}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Save changes">
                    <IconButton size="small" onClick={handleSave} color="primary" disabled={saving}>
                      {saving ? <CircularProgress size={18} /> : <Save fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>

          {/* Column List */}
          <Box sx={{ overflow: 'auto', flex: 1, p: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={allColumns.map((col) => col.field)} strategy={verticalListSortingStrategy}>
                  <List dense sx={{ py: 0 }}>
                    {allColumns.map((col) => (
                      <SortableColumnItem key={col.field} column={col} onToggleVisibility={onToggleVisibility} />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            )}
          </Box>

          {/* Footer with instructions */}
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Drag to reorder. Check/uncheck to show/hide.
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  )
}
